import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../Config/api';

export default function AssetVerifyPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get('ref');
  const [status, setStatus] = useState('loading'); // loading | success | expired | error
  const [message, setMessage] = useState('');
  const [assetInfo, setAssetInfo] = useState(null);

  const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const defaultLogoUrl = `${apiBaseUrl}/api/auth/media/branding/a-sight_LOGO_DEF.png`;
  const [brandingLogoUrl, setBrandingLogoUrl] = useState(defaultLogoUrl);

  const calledOnceRef = useRef(false);

  useEffect(() => {
    const url = ref
      ? `${API_ENDPOINTS.brandingLogo}?ref=${encodeURIComponent(ref)}`
      : API_ENDPOINTS.brandingLogo;
    fetch(url)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.logo_url) setBrandingLogoUrl(data.logo_url);
      })
      .catch(() => {});
  }, [ref]);

  const verifyAsset = async () => {
    if (!token) {
      setStatus('error');
      setMessage('Link di verifica non valido.');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const res = await fetch(API_ENDPOINTS.assetVerifyByToken(token));
      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        setStatus('success');
        setMessage(data.detail || 'Asset verificato con successo.');
        setAssetInfo({ asset: data.asset, tipo_asset: data.tipo_asset });
        return;
      }

      if (res.status === 410) {
        setStatus('expired');
        setMessage(data.detail || 'Il link di verifica è scaduto.');
        return;
      }

      setStatus('error');
      setMessage(data.detail || 'Impossibile completare la verifica.');
    } catch {
      setStatus('error');
      setMessage('Errore di connessione. Riprova tra qualche istante.');
    }
  };

  useEffect(() => {
    if (!calledOnceRef.current) {
      calledOnceRef.current = true;
      verifyAsset();
    }
  }, [token]);

  const isLoggedIn = !!sessionStorage.getItem('access_token');

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white p-8 rounded shadow-md w-full max-w-md text-center space-y-6">
        <img
          src={brandingLogoUrl}
          alt="Logo"
          draggable="false"
          style={{ maxWidth: 120, margin: '0 auto', display: 'block', userSelect: 'none' }}
          className="mb-2"
        />

        {status === 'loading' && (
          <>
            <p className="text-gray-700 text-lg">Verifica monitoraggio in corso...</p>
            <div className="animate-spin h-6 w-6 border-4 border-blue-500 border-t-transparent rounded-full mx-auto" />
          </>
        )}

        {status === 'success' && (
          <>
            <h2 className="text-xl font-bold text-green-600">Monitoraggio autorizzato</h2>
            <p className="text-gray-600">{message}</p>
            {assetInfo?.asset && (
              <p className="text-sm text-gray-500">
                Asset: <strong>{assetInfo.asset}</strong>
              </p>
            )}
            <button
              type="button"
              onClick={() => navigate(isLoggedIn ? '/myassets' : '/')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
            >
              {isLoggedIn ? 'Vai ai miei asset' : 'Vai al login'}
            </button>
          </>
        )}

        {status === 'expired' && (
          <>
            <h2 className="text-xl font-bold text-amber-600">Link scaduto</h2>
            <p className="text-gray-600">{message}</p>
            <p className="text-sm text-gray-500">
              Accedi alla piattaforma e usa &quot;Reinvia email di verifica&quot; dalla pagina Asset.
            </p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
            >
              Vai al login
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <h2 className="text-xl font-bold text-red-600">Verifica non riuscita</h2>
            <p className="text-gray-600">{message}</p>
            <button
              type="button"
              onClick={verifyAsset}
              className="mt-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded"
            >
              Riprova
            </button>
          </>
        )}
      </div>
    </div>
  );
}
